import React from 'react';
import {
  posCapabilities,
  posCapabilitiesEyebrow,
  posCapabilitiesHeading,
  posIntegrationCards,
  posScreenshotsNote,
  posWorkflowChips,
  posWorkflowEyebrow,
  posWorkflowHeading,
  posWorkflowIntro,
  posWorkflowSteps,
} from '../../content/siteMessaging';

type WorkflowStep = {
  image: string;
  width: number;
  height: number;
  alt: string;
  frameClassName: string;
  imageClassName: string;
};

const workflowSteps: WorkflowStep[] = [
  {
    image: '/screenshots/pos-e2e/03-qr-generated-ipad.png',
    width: 1640,
    height: 2360,
    alt: 'Coupling POS on a tablet showing a generated QR code for checkout. Devices in photos are a demo of the app.',
    frameClassName: 'h-[17rem] sm:h-[18rem]',
    imageClassName: 'h-full w-full object-cover object-[center_14%]',
  },
  {
    image: '/screenshots/pos-e2e/07-iphone-pay-button.png',
    width: 1206,
    height: 2622,
    alt: 'Customer phone showing a pay button with rewards covering part of the sale',
    frameClassName: 'mx-auto h-[17rem] max-w-[14rem] sm:h-[18rem]',
    imageClassName: 'h-full w-full object-cover object-[center_36%]',
  },
  {
    image: '/screenshots/pos-e2e/08-pos-received-tokens-ipad.png',
    width: 1640,
    height: 2360,
    alt: 'POS payment summary showing rewards received and remaining cash on a mixed-tender sale',
    frameClassName: 'h-[17rem] sm:h-[18rem]',
    imageClassName: 'h-full w-full object-cover object-[center_15%]',
  },
  {
    image: '/screenshots/pos-e2e/09-pos-reward-credited-ipad.png',
    width: 1640,
    height: 2360,
    alt: 'Reward credited screen showing loyalty issued after the merchant completes a mixed-tender payment',
    frameClassName: 'h-[17rem] sm:h-[18rem]',
    imageClassName: 'h-full w-full object-cover object-[center_14%]',
  },
];

const chipClassName =
  'rounded-full border border-brand-primary/15 bg-white px-4 py-2 text-sm font-medium text-neutral-700 shadow-sm';
const sideCardClassName =
  'rounded-[1.75rem] border border-brand-primary/12 bg-white px-5 py-5 shadow-[0_20px_50px_rgba(130,64,29,0.08)]';

export const PosWorkflowSection: React.FC = () => {
  return (
    <section id="pos-workflow" className="bg-[#f8efe6] px-4 py-24">
      <div className="mx-auto max-w-6xl">
        <div className="max-w-3xl">
          <span className="inline-flex rounded-full bg-white px-4 py-1 text-sm font-semibold text-brand-primary shadow-sm">
            {posWorkflowEyebrow}
          </span>
          <h2 className="mt-6 text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl">
            {posWorkflowHeading}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-neutral-600">
            {posWorkflowIntro}
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          {posWorkflowChips.map((chip) => (
            <span key={chip} className={chipClassName}>
              {chip}
            </span>
          ))}
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="rounded-[2rem] border border-brand-primary/12 bg-white p-6 shadow-[0_24px_60px_rgba(130,64,29,0.10)]">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-brand-primary">
              {posCapabilitiesEyebrow}
            </p>
            <p className="mt-3 text-3xl font-semibold tracking-tight text-neutral-900">
              {posCapabilitiesHeading}
            </p>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2">
              {posCapabilities.map((capability) => (
                <li
                  key={capability.title}
                  className="rounded-[1.5rem] border border-[#ecd7c5] bg-[#fffaf4] p-5"
                >
                  <h3 className="text-lg font-semibold text-neutral-900">
                    {capability.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">
                    {capability.description}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid content-start gap-4">
            {posIntegrationCards.map((card) => (
              <article key={card.title} className={sideCardClassName}>
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-brand-primary">
                  {card.title}
                </p>
                <p className="mt-3 text-base leading-relaxed text-neutral-700">
                  {card.description}
                </p>
              </article>
            ))}
          </div>
        </div>

        <p className="mt-10 max-w-3xl rounded-[1.5rem] border border-brand-primary/12 bg-white px-5 py-4 text-sm leading-relaxed text-neutral-600 shadow-sm">
          {posScreenshotsNote}
        </p>

        <div className="mt-12 grid gap-8 md:grid-cols-2 xl:grid-cols-4">
          {workflowSteps.map((step, index) => {
            const copy = posWorkflowSteps[index];
            return (
              <article
                key={copy.title}
                className="rounded-[2rem] border border-brand-primary/10 bg-white p-5 shadow-[0_24px_60px_rgba(130,64,29,0.12)]"
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-primary text-base font-semibold text-white">
                    {index + 1}
                  </span>
                  <span className="rounded-full bg-[#fff1e1] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-primary">
                    {copy.label}
                  </span>
                </div>
                <div
                  className={`mt-5 overflow-hidden rounded-[1.5rem] border border-neutral-200 bg-[#fffaf4] ${step.frameClassName}`}
                >
                  <img
                    src={step.image}
                    alt={step.alt}
                    width={step.width}
                    height={step.height}
                    className={step.imageClassName}
                    loading="lazy"
                  />
                </div>
                <h3 className="mt-5 text-xl font-semibold text-neutral-900">
                  {copy.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-600">
                  {copy.description}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PosWorkflowSection;
